(() => {
  'use strict';

  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready === 'true') return;
      this.dataset.ready = 'true';
      this.input = this.querySelector('[data-predictive-search-input]');
      this.results = this.querySelector('[data-predictive-search-results]');
      this.status = this.querySelector('[data-predictive-search-status]');
      this.clearButton = this.querySelector('[data-search-clear]');
      this.abortController = null;
      this.cache = new Map();
      this.timer = null;
      if (!this.input || !this.results) return;

      this.input.addEventListener('input', () => {
        window.clearTimeout(this.timer);
        this.toggleClear();
        this.timer = window.setTimeout(() => this.search(), 220);
      });
      this.input.addEventListener('keydown', (event) => this.onKeydown(event));
      this.results.addEventListener('keydown', (event) => this.onResultsKeydown(event));
      this.clearButton?.addEventListener('click', () => this.clear());
      this.toggleClear();
    }

    toggleClear() {
      if (this.clearButton) this.clearButton.hidden = this.input.value.length === 0;
    }

    clear() {
      this.input.value = '';
      this.close();
      this.toggleClear();
      this.input.focus();
    }

    close() {
      this.abortController?.abort();
      this.results.innerHTML = '';
      this.input.setAttribute('aria-expanded', 'false');
      if (this.status) this.status.textContent = '';
    }

    async search() {
      const term = this.input.value.trim();
      if (term.length < 2) return this.close();
      if (this.cache.has(term)) return this.render(this.cache.get(term));

      this.abortController?.abort();
      this.abortController = new AbortController();
      this.setLoading();
      const route = window.PurestWipes?.routes?.predictiveSearch || '/search/suggest';
      const params = new URLSearchParams({
        q: term,
        'resources[type]': 'product,collection,page,article,query',
        'resources[limit]': '6',
        'resources[options][unavailable_products]': 'last',
        section_id: 'predictive-search'
      });

      try {
        const response = await fetch(`${route}?${params}`, { signal: this.abortController.signal });
        if (!response.ok) throw new Error(`Predictive search failed: ${response.status}`);
        const html = await response.text();
        const documentFragment = new DOMParser().parseFromString(html, 'text/html');
        const content = documentFragment.querySelector('[data-predictive-results-content]');
        const markup = content ? content.outerHTML : '';
        this.cache.set(term, markup);
        this.render(markup);
      } catch (error) {
        if (error.name !== 'AbortError') this.close();
      }
    }

    setLoading() {
      const message = window.PurestWipes?.strings?.searchLoading || 'Searching…';
      if (this.status) this.status.textContent = message;
      this.input.setAttribute('aria-expanded', 'true');
    }

    render(markup) {
      this.results.innerHTML = markup;
      this.input.setAttribute('aria-expanded', markup ? 'true' : 'false');
      if (this.status) {
        const count = this.results.querySelectorAll('a[href]').length;
        this.status.textContent = count ? `${count} search suggestion${count === 1 ? '' : 's'} available.` : 'No search suggestions found.';
      }
    }

    onKeydown(event) {
      const links = [...this.results.querySelectorAll('a')];
      if (!links.length || !['ArrowDown', 'ArrowUp'].includes(event.key)) return;
      event.preventDefault();
      const next = event.key === 'ArrowDown' ? links[0] : links.at(-1);
      next.tabIndex = 0;
      next.focus();
    }

    onResultsKeydown(event) {
      if (!['ArrowDown', 'ArrowUp', 'Escape'].includes(event.key)) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.input.focus();
        this.close();
        return;
      }
      const links = [...this.results.querySelectorAll('a[href]')];
      const index = links.indexOf(document.activeElement);
      if (index < 0) return;
      event.preventDefault();
      const offset = event.key === 'ArrowDown' ? 1 : -1;
      links[(index + offset + links.length) % links.length].focus();
    }
  }

  if (!customElements.get('predictive-search')) customElements.define('predictive-search', PredictiveSearch);
})();
